import { useCallback, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useRouter } from '@tanstack/react-router';
import { Eye, Inbox, Layers } from 'lucide-react';
import { DataTable, DataTablePagination } from '@/components/common/DataTable';
import { ErrorState } from '@/components/common/ErrorState';
import { Button, buttonVariants } from '@/components/ui/button';
import { useKeepPageInRange } from '@/hooks/useKeepPageInRange';
import { cn } from '@/lib/utils';
import { PcmpHoldActionButtons } from '../PcmpHoldActions/PcmpHoldActionButtons';
import { PCMP_PERMISSION } from '../../api/pcmpAccess.api';
import {
  pcmpAccountsCountsQueryOptions,
  pcmpAccountsQueueQueryOptions,
} from '../../api/pcmpAccounts.api';
import {
  PCMP_ACCOUNTS_EMPTY_MESSAGE,
  PCMP_ACCOUNTS_TAB_LABELS,
  PCMP_REDISBURSE_ACTION_COPY,
} from '../../constants/pcmpAccounts.constants';
import { PCMP_QUEUE_PAGE_SIZE_OPTIONS } from '../../constants/pcmpQueue.constants';
import type { PcmpAccountsFilterController } from '../../hooks/usePcmpAccountsFilters';
import { usePcmpPermissions } from '../../hooks/usePcmpPermissions';
import { usePcmpRowSelection } from '../../hooks/usePcmpRowSelection';
import type { PcmpAccountsQueueItem, PcmpAccountsTab } from '../../types/pcmpAccounts.types';
import { PcmpAccountsActionButtons } from '../PcmpAccountsActions/PcmpAccountsActionButtons';
import {
  PcmpRedisburseBulkDialog,
  type PcmpRedisburseBulkTarget,
} from '../PcmpAccountsActions/PcmpRedisburseBulkDialog';
import { PcmpSelectionBar } from '../PcmpBatch/PcmpSelectionBar';
import { pcmpSelectColumn } from '../PcmpQueue/pcmpSelectColumn';
import { getPcmpAccountsColumns } from './PcmpAccountsColumns';

interface PcmpAccountsItemsViewProps {
  controller: PcmpAccountsFilterController;
  tab: PcmpAccountsTab;
}

/** An item tab's proposals (failed, reverified, returned, completed, on hold). */
export function PcmpAccountsItemsView({ controller, tab }: PcmpAccountsItemsViewProps) {
  const router = useRouter();
  const { pageParams, appliedFilters, limit } = controller;
  const { can } = usePcmpPermissions();
  const canAct = can(PCMP_PERMISSION.ACCOUNTS, PCMP_PERMISSION.ACT);
  // Completed rows are paid: Accounts can fail them after payout, and Admin /
  // MIS return them, all or part (LDS-765). The row carries no capability, so
  // offer both and let the API refuse a held or part-returned one.
  const canFailPaid = tab === 'COMPLETED' && canAct;
  const canReturnPaid =
    tab === 'COMPLETED' && can(PCMP_PERMISSION.REQUEST_APPROVE, PCMP_PERMISSION.ACT);
  // Reverified rows are corrected, unheld and not yet batched: all can be picked.
  const canBatch = tab === 'REVERIFIED' && canAct;

  const selection = usePcmpRowSelection(JSON.stringify([tab, appliedFilters]));
  const [bulkTarget, setBulkTarget] = useState<PcmpRedisburseBulkTarget | null>(null);
  // The tabs' counts under the same filters: the totals "select all" covers.
  const { data: counts } = useQuery({
    ...pcmpAccountsCountsQueryOptions(appliedFilters),
    enabled: canBatch,
  });

  const { data, isLoading, isFetching, error, refetch } = useQuery(
    pcmpAccountsQueueQueryOptions({ ...pageParams, tab }),
  );

  useKeepPageInRange(pageParams.page, data?.meta.totalPages, controller.setPage);

  const openProposal = useCallback(
    (item: PcmpAccountsQueueItem) => {
      void router.navigate({
        to: '/pcmp/proposals/$proposalId',
        params: { proposalId: item.proposalId },
      });
    },
    [router],
  );

  const rows = useMemo(() => data?.data ?? [], [data]);

  const columns = getPcmpAccountsColumns<PcmpAccountsQueueItem>({
    tab,
    leading: canBatch ? [pcmpSelectColumn<PcmpAccountsQueueItem>({ selection })] : [],
    renderActions: (item) => (
      <>
        <PcmpAccountsActionButtons
          proposalId={item.proposalId}
          refNo={item.refNo}
          fail={canFailPaid ? { stage: 'POST_PAYOUT' } : null}
          canReturn={canReturnPaid}
          className="flex-nowrap"
        />
        <PcmpHoldActionButtons
          proposalId={item.proposalId}
          refNo={item.refNo}
          canHold={item.canHold}
          canRelease={item.canRelease}
          hold={item.hold}
          className="flex-nowrap"
        />
        <Link
          to="/pcmp/proposals/$proposalId"
          params={{ proposalId: item.proposalId }}
          className={cn(buttonVariants({ variant: 'outline', size: 'sm' }), 'gap-1.5')}
        >
          <Eye className="size-3.5" />
          View
        </Link>
      </>
    ),
  });

  const matching = { count: counts?.reverified ?? 0, amount: counts?.reverifiedAmount ?? 0 };
  const summary = selection.summarize(matching.count, matching.amount);
  const isPageSelected =
    rows.length > 0 && rows.every((row) => selection.isSelected(row.proposalId));
  const refNoOf = (proposalId: number) => rows.find((row) => row.proposalId === proposalId)?.refNo;

  const startCreate = () =>
    setBulkTarget({
      action: 'CREATE',
      count: summary.count,
      amount: summary.amount,
      scope: selection.allMatching
        ? 'All corrected proposals matching the filters'
        : 'Selected proposals',
      selection: selection.allMatching
        ? {
            selectAll: 'MATCHING',
            filters: appliedFilters,
            excludedProposalIds: selection.pickedIds,
          }
        : { proposalIds: selection.pickedIds },
    });

  const openBatch = (batchNo: number) => {
    void router.navigate({
      to: '/pcmp/accounts/redisburse-batches/$batchNo',
      params: { batchNo },
      search: { view: 'PENDING' },
    });
  };

  if (error) {
    return (
      <ErrorState
        error={error}
        fallbackTitle={`Could not load ${PCMP_ACCOUNTS_TAB_LABELS[tab]}`}
        onRetry={() => void refetch()}
        isRetrying={isFetching}
        fill
      />
    );
  }

  return (
    <>
      {canBatch && rows.length > 0 ? (
        <PcmpSelectionBar
          selection={selection}
          summary={summary}
          selectAll={
            counts ? { total: matching.count, amount: matching.amount, isPageSelected } : undefined
          }
          hint="Tick corrected proposals to gather them into a re-disbursement batch."
        >
          <Button
            type="button"
            size="sm"
            onClick={startCreate}
            disabled={summary.count === 0}
            className="gap-1.5"
          >
            <Layers className="size-3.5" />
            {PCMP_REDISBURSE_ACTION_COPY.CREATE.label}
          </Button>
        </PcmpSelectionBar>
      ) : null}
      <DataTable
        data={rows}
        columns={columns}
        isLoading={isLoading}
        stickyFirstColumn
        fillHeight
        className={cn('lg:min-h-0 lg:flex-1', isFetching && !isLoading && 'opacity-70')}
        emptyIcon={Inbox}
        emptyMessage={PCMP_ACCOUNTS_EMPTY_MESSAGE[tab]}
        skeletonRowCount={Math.min(limit, 15)}
        getRowClassName={(item) =>
          canBatch && selection.isSelected(item.proposalId) ? 'bg-primary/5' : undefined
        }
        onRowClick={openProposal}
      />
      {data?.meta ? (
        <DataTablePagination
          page={data.meta.page}
          totalPages={data.meta.totalPages}
          total={data.meta.total}
          limit={data.meta.limit}
          onPageChange={controller.setPage}
          limitSelect={{ options: PCMP_QUEUE_PAGE_SIZE_OPTIONS, onChange: controller.setLimit }}
        />
      ) : null}
      {bulkTarget ? (
        <PcmpRedisburseBulkDialog
          target={bulkTarget}
          refNoOf={refNoOf}
          onDone={selection.clear}
          onCreated={openBatch}
          onClose={() => setBulkTarget(null)}
        />
      ) : null}
    </>
  );
}
